import { Component, EventEmitter, Inject, Input, Optional, Output, Self, TemplateRef, ViewChild } from '@angular/core';
import { NgControl } from '@angular/forms';
import { FileViewModel } from '../../../../modules/tcm-core.module';
import { TranslationService } from '../../../../services/translation.service';
import { TcmModalService } from '../../services/tcm-modal.service';
import { TcmAppSettings } from '../../../../common/tcm-app-settings';
import { SystemPreferences, SYSTEM_PREFERENCES } from '../../../../common/system-preferences';
import { FormControlBase } from '../../../../modules/tcm-form/common/form-control-base/form-control-base';

@Component({
    selector: 'tcm-file-input',
    templateUrl: './tcm-file-input.component.html',
    styleUrls: ['./tcm-file-input.component.scss'],
    inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
    standalone: false
})
export class TcmFileInputComponent extends FormControlBase<FileViewModel | FileViewModel[]>  {

    @ViewChild('files') filesRef: any;
    @ViewChild('fileReadErrors', { static: true }) fileReadErrorsTemplate: TemplateRef<any>;

    @Input() acceptedTypes: string[] = [];
    @Input() maxSizeMb: number;
    @Input() isMultiple: boolean;
    @Output() filesChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() attachmentClick = new EventEmitter<FileViewModel>();
    fileExtensions: string;
    uploadFieldName: string = "";

    attachedFiles: FileViewModel[] = [];

    isLoading: boolean;

    constructor(
        @Self() @Optional() protected override ngModel: NgControl,
        private modalService: TcmModalService,
        private translationService: TranslationService,
        @Inject(SYSTEM_PREFERENCES) private systemPreferences: SystemPreferences
    ) {        
        super('tcm-file-input', ngModel);              
        this.maxSizeMb = this.systemPreferences.fileUploadSize;
        this.fileExtensions = this.systemPreferences.fileExtensions;
    }

    onFilesChange(name: string, files: any, e: any) {
        let allowedBytes = this.maxSizeMb * 1024 * 1024;
        for (let file of files) {

            let error = null;
            
            const allowedFileExtensions = this.fileExtensions.split(" ").filter(x => x.length > 0);    
    
            if (!this.acceptedTypes || this.acceptedTypes.length == 0) {
                this.acceptedTypes = allowedFileExtensions;
            }                
            
          if (this.acceptedTypes && this.acceptedTypes.length > 0 && !this.acceptedTypes.some(x => x === '*')) {
                let extension = file.name.substring(files[0].name.lastIndexOf('.'));

                if (!this.acceptedTypes.some(at => at.toLowerCase() === extension.toLowerCase())) {
                    e.target.value = null;
                    error = this.translationService.translate(x => x.Messages.ErrorMessages.FileTypeUnsupported).replace('{0}', extension);
                }
            }

            if (error == null && file.size > allowedBytes) {
                error = this.translationService.translate(x => x.Messages.ErrorMessages.FileSizeLimitReached)
                    .replace('{0}', file.name)
                    .replace('{1}', this.maxSizeMb);
            }

            if (error != null) {
                e.preventDefault();

                this.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error, error);

                return;
            }
        }


        this.isLoading = true;
        this.ngModel.control.markAsPending({ onlySelf: true });

        const promises: Promise<any>[] = [];
        const newAttachments: FileViewModel[] = [];

        const fileReadErrors: string[] = [];

        for (let file of files) {

            var promise = readFileAsync(file).then((fileObj) => {
                if (this.isMultiple) {
                    this.attachedFiles.push(fileObj);
                }
                else {
                    this.attachedFiles.splice(0, 1, fileObj);
                }

                newAttachments.push(fileObj);

                return Promise.resolve();
            }, (error) => {
                let readError = this.translationService.translate(x => x.Messages.ErrorMessages.FileReadError).replace('{0}', file.name);
                readError += ` ${error}`;
                fileReadErrors.push(readError);

                return Promise.resolve();
            });

            promises.push(promise);
        }

        Promise.all(promises).finally(() => {
            this.filesRef.nativeElement.value = "";
            this.isLoading = false;
            if (this.isMultiple) {
                const data = [];
                data.push(...this.value as FileViewModel[])
                Object.assign(data, newAttachments);
                this.value = data;
            }
            else {
                this.value = newAttachments[0];
            }

            if (fileReadErrors.length) {
                this.modalService.showErrorModalAsync(
                    x => x.Messages.MessageHeadersAndActions.Error,
                    this.fileReadErrorsTemplate, 'sm', null,
                    { errors: fileReadErrors });
            }
        });
    }

    onAttachmentClick($event, attachment: FileViewModel) {
        $event.stopPropagation();

        if (attachment.id) {
            this.attachmentClick.emit(attachment);
        }
    }

    onRemoveAttachment($event, attachment: FileViewModel) {
        $event.stopPropagation();

        this.attachedFiles.splice(this.attachedFiles.indexOf(attachment), 1);

        if (this.isMultiple) {
            (this.value as FileViewModel[]).splice((this.value as FileViewModel[]).indexOf(attachment), 1);
        }
        else {
            this.value = null;
        }
    }

    protected override onValueWritten() {
        if (this.isMultiple) {
            const files = this.value as FileViewModel[];

            this.attachedFiles.splice(0, this.attachedFiles.length, ...files);
        }
        else {
            const file = this.value as FileViewModel;
            if (file) {
                this.attachedFiles.splice(0, this.attachedFiles.length, file);
            }
            else {
                this.attachedFiles.splice(0, this.attachedFiles.length);
            }
        }
    }
}

function readFileAsync(file: any): Promise<FileViewModel> {
    return new Promise<any>((resolve, reject) => {

        const reader = new FileReader();

        reader['fileName'] = file.name;
        reader['mimeType'] = file.type;
        reader.onerror = (e) => {
            reject(e);
        };

        reader.onloadend = ((e) => {
            const arrayBuffer = (reader.result as ArrayBuffer);

            const array = new Uint8Array(arrayBuffer);

            const bytes = Array.from(array);

            const fileObj = {} as FileViewModel;

            fileObj.name = reader['fileName'];
            fileObj.content = bytes;
            fileObj.mimeType = reader['mimeType'];

            resolve(fileObj);
        });
        reader.readAsArrayBuffer(file);
    });
}
